import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { CheckCircle, XCircle, MagnifyingGlass, Star } from "@phosphor-icons/react";

export default function Tap2Verify() {
  const [items, setItems] = useState([]);
  const [source, setSource] = useState("");      // "", "curated", "crowdsource"
  const [status, setStatus] = useState("new");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (source) params.append("source", source);
    if (status) params.append("status", status);
    api.get(`/tap2verify?${params.toString()}`).then(r => setItems(r.data || [])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [source, status]);

  const promote = async (id) => {
    const rating = parseFloat(prompt("Curator rating 0–5?", "4") || "0");
    const notes = prompt("Curator notes (optional)") || "";
    await api.post(`/tap2verify/${id}/promote`, { rating, curator_notes: notes });
    load();
  };
  const dismiss = async (id) => {
    if (!window.confirm("Dismiss this report?")) return;
    await api.post(`/tap2verify/${id}/dismiss`);
    load();
  };

  return (
    <div className="p-6 space-y-4" data-testid="tap2verify-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><MagnifyingGlass size={12}/> Tap2Verify</div>
          <h1 className="font-display font-bold text-3xl mt-1">Curated &amp; crowdsourced verifications</h1>
          <div className="text-[12px] text-[var(--span-text-2)] mt-1">
            Public crowdsource flags (anyone can report) + curator-promoted curated verifications used in the Field Marketplace.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="t2v-filter-source" className="text-[12px] hairline px-2 py-1 surface"
                  value={source} onChange={e => setSource(e.target.value)}>
            <option value="">All sources</option>
            <option value="crowdsource">Crowdsource</option>
            <option value="curated">Curated</option>
          </select>
          <select data-testid="t2v-filter-status" className="text-[12px] hairline px-2 py-1 surface"
                  value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">All</option>
            <option value="new">New</option>
            <option value="verified">Verified</option>
            <option value="dismissed">Dismissed</option>
          </select>
        </div>
      </div>

      <div className="card-flat p-0 overflow-hidden">
        <table className="w-full text-[12px]">
          <thead className="bg-[var(--span-surface-3)] text-[10px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Brand</th>
              <th className="text-left px-3 py-2">Seller / Listing</th>
              <th className="text-left px-3 py-2">Country</th>
              <th className="text-left px-3 py-2">Source</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Rating</th>
              <th className="text-left px-3 py-2">Reporter</th>
              <th className="text-left px-3 py-2">When</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="px-3 py-6 text-center text-[var(--span-text-2)]">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-[var(--span-text-2)]">No reports.</td></tr>
            )}
            {items.map(v => (
              <tr key={v.verification_id} className="border-t hairline" data-testid={`t2v-row-${v.verification_id}`}>
                <td className="px-3 py-2 font-bold">{v.brand}</td>
                <td className="px-3 py-2 max-w-xs truncate" title={v.seller_or_listing}>{v.seller_or_listing}</td>
                <td className="px-3 py-2">{v.country_code}</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 ${v.source === "curated" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{v.source}</span>
                </td>
                <td className="px-3 py-2"><span className="text-[10px] uppercase">{v.status}</span></td>
                <td className="px-3 py-2 font-mono tnum">
                  {v.rating > 0 && (<><Star size={10} weight="fill" className="inline mr-0.5 text-amber-500"/>{v.rating.toFixed(1)}</>)}
                </td>
                <td className="px-3 py-2 text-[var(--span-text-2)] truncate max-w-[140px]">{v.reporter_name || v.reporter_email || "anon"}</td>
                <td className="px-3 py-2 text-[var(--span-text-2)]">{fmtDate(v.created_at)}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {v.source !== "curated" && (
                    <button data-testid={`t2v-promote-${v.verification_id}`} onClick={() => promote(v.verification_id)}
                            className="text-[10px] hairline px-2 py-1 hover:bg-emerald-50 mr-1">
                      <CheckCircle size={10} weight="duotone" className="inline mr-0.5"/>Promote
                    </button>
                  )}
                  {v.status !== "dismissed" && (
                    <button data-testid={`t2v-dismiss-${v.verification_id}`} onClick={() => dismiss(v.verification_id)}
                            className="text-[10px] hairline px-2 py-1 hover:bg-red-50">
                      <XCircle size={10} weight="duotone" className="inline mr-0.5"/>Dismiss
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
